import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { fetchChatConversations } from '../api/chat';
import { fetchCompanions } from '../api/companions';
import { fetchNights } from '../api/nights';
import TavernChatPanel from '../components/chat/TavernChatPanel';
import PresenceBadge from '../components/companions/PresenceBadge';
import { getInitials } from '../components/companions/presence';

const conversationTitle = (conversation, t) => {
  if (conversation.type === 'direct') {
    const other = (conversation.participants || []).find(
      (participant) => participant.id !== conversation.viewerId
    );
    return other?.displayName || t('chat.inbox.directFallback');
  }

  if (conversation.type === 'night') {
    return conversation.title || t('chat.inbox.nightFallback');
  }

  return conversation.title || t('chat.context.friendsTitle');
};

const conversationSubtitle = (conversation, t) => {
  if (conversation.lastMessage?.body) {
    const sender = conversation.lastMessage.senderName || t('chat.inbox.someone');
    return `${sender}: ${conversation.lastMessage.body}`;
  }

  if (conversation.type === 'night') return t('chat.inbox.emptyNight');
  if (conversation.type === 'direct') return t('chat.inbox.emptyDirect');
  return t('chat.inbox.emptyGroup');
};

const ConversationAvatar = ({ conversation, title }) => {
  if (conversation.type === 'direct') {
    const other = (conversation.participants || []).find(
      (participant) => participant.id !== conversation.viewerId
    );

    return (
      <Avatar src={other?.avatarUrl || undefined} sx={{ bgcolor: 'rgba(15, 76, 69, 0.18)' }}>
        {other?.avatarUrl ? null : getInitials(title)}
      </Avatar>
    );
  }

  return (
    <Avatar sx={{ bgcolor: 'secondary.light', color: 'primary.dark' }}>
      <GroupIcon />
    </Avatar>
  );
};

const ConversationRow = ({ conversation, onOpen, t }) => {
  const title = conversationTitle(conversation, t);
  const subtitle = conversationSubtitle(conversation, t);
  const directCompanion = conversation.type === 'direct'
    ? (conversation.participants || []).find(
        (participant) => participant.id !== conversation.viewerId
      )
    : null;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(conversation)}
      sx={{
        width: '100%',
        appearance: 'none',
        border: '1px solid',
        borderColor: 'rgba(15, 76, 69, 0.16)',
        borderRadius: 2,
        bgcolor: 'rgba(255, 250, 240, 0.72)',
        color: 'text.primary',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: { xs: 1.5, sm: 2 },
        textAlign: 'left',
        boxShadow: '0 16px 34px -30px rgba(45, 26, 11, 0.65)',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'rgba(255, 250, 240, 0.92)',
        },
      }}
    >
      <Badge
        color="secondary"
        overlap="circular"
        badgeContent={conversation.unreadCount}
        invisible={!conversation.unreadCount}
      >
        <ConversationAvatar conversation={conversation} title={title} />
      </Badge>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35 }}>
          <Typography
            variant="h6"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.1,
            }}
          >
            {title}
          </Typography>
          {conversation.unreadCount > 0 ? (
            <Chip
              size="small"
              label={t('chat.inbox.unread', { count: conversation.unreadCount })}
              sx={{ height: 22, fontWeight: 900 }}
            />
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </Typography>
        {directCompanion?.presence ? (
          <Box sx={{ mt: 0.75 }}>
            <PresenceBadge presence={directCompanion.presence} size="small" noWrap />
          </Box>
        ) : null}
      </Box>
      <ChevronRightIcon sx={{ color: 'text.secondary', flex: '0 0 auto' }} />
    </Box>
  );
};

const ConversationSection = ({ action, conversations, empty, icon, onOpen, title, t }) => (
  <Box component="section" sx={{ mb: { xs: 4, md: 5 } }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 900, letterSpacing: '0.12em' }}
        >
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>

    {conversations.length ? (
      <Stack spacing={1.25}>
        {conversations.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            onOpen={onOpen}
            t={t}
          />
        ))}
      </Stack>
    ) : (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'rgba(15, 76, 69, 0.24)',
          borderRadius: 2,
          p: 2.5,
          color: 'text.secondary',
          bgcolor: 'rgba(255, 250, 240, 0.42)',
        }}
      >
        <Typography>{empty}</Typography>
      </Box>
    )}
  </Box>
);

const formatNightDate = (value, language) => {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(language || 'fr', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const START_COMPANIONS_PAGE_SIZE = 5;

const StartChatDialog = ({
  companions,
  companionsHasMore,
  companionsLoading,
  loading,
  nights,
  onClose,
  onLoadMoreCompanions,
  onSearchCompanions,
  onSelectCompanion,
  onSelectNight,
  open,
  searchQuery,
  language,
  t,
}) => {
  const locale = language || 'fr';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        {t('chat.startDialog.title')}
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <PersonOutlineIcon color="primary" />
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900 }}>
                  {t('chat.startDialog.companions')}
                </Typography>
              </Stack>
              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                onChange={(event) => onSearchCompanions(event.target.value)}
                placeholder={t('chat.startDialog.searchCompanions')}
                sx={{ mb: 1.25 }}
              />
              {companions.length ? (
                <Stack spacing={1}>
                  {companions.map((companion) => (
                    <Box
                      key={companion.id}
                      component="button"
                      type="button"
                      onClick={() => onSelectCompanion(companion)}
                      sx={{
                        appearance: 'none',
                        width: '100%',
                        border: '1px solid',
                        borderColor: 'rgba(15, 76, 69, 0.16)',
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 250, 240, 0.72)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        textAlign: 'left',
                      }}
                    >
                      <Avatar src={companion.avatarUrl || undefined}>
                        {companion.avatarUrl ? null : getInitials(companion.displayName || companion.username)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 900 }} noWrap>
                          {companion.displayName || companion.username}
                        </Typography>
                        {companion.presence ? (
                          <PresenceBadge presence={companion.presence} size="small" noWrap />
                        ) : null}
                      </Box>
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                  ))}
                  {companionsHasMore ? (
                    <Button
                      variant="outlined"
                      disabled={companionsLoading}
                      onClick={onLoadMoreCompanions}
                    >
                      {companionsLoading ? (
                        <CircularProgress size={18} />
                      ) : (
                        t('chat.startDialog.loadMoreCompanions')
                      )}
                    </Button>
                  ) : null}
                </Stack>
              ) : companionsLoading ? (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  {searchQuery
                    ? t('chat.startDialog.noCompanionResults')
                    : t('chat.startDialog.noCompanions')}
                </Typography>
              )}
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <GroupIcon color="primary" />
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900 }}>
                  {t('chat.startDialog.nights')}
                </Typography>
              </Stack>
              {nights.length ? (
                <Stack spacing={1}>
                  {nights.map((night) => (
                    <Box
                      key={night.id}
                      component="button"
                      type="button"
                      onClick={() => onSelectNight(night)}
                      sx={{
                        appearance: 'none',
                        width: '100%',
                        border: '1px solid',
                        borderColor: 'rgba(15, 76, 69, 0.16)',
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 250, 240, 0.72)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        textAlign: 'left',
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'secondary.light', color: 'primary.dark' }}>
                        <GroupIcon />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 900 }} noWrap>
                          {night.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatNightDate(night.scheduledAt, locale)}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">{t('chat.startDialog.noNights')}</Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ChatPage = () => {
  const { i18n, t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [startCompanions, setStartCompanions] = useState([]);
  const [startCompanionsHasMore, setStartCompanionsHasMore] = useState(false);
  const [startCompanionsLoading, setStartCompanionsLoading] = useState(false);
  const [startCompanionsPage, setStartCompanionsPage] = useState(1);
  const [startCompanionQuery, setStartCompanionQuery] = useState('');
  const [startNights, setStartNights] = useState([]);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [startDialogLoading, setStartDialogLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const closedConversationIdRef = useRef(null);
  const startCompanionsRequestRef = useRef(0);
  const skipNextStartCompanionSearchRef = useRef(false);
  const requestedConversationId = Number(searchParams.get('conversationId'));
  const pushScrollToken = searchParams.get('scroll') === 'latest'
    ? searchParams.get('pushTs') || 'latest'
    : '';

  const loadConversations = useCallback(async () => {
    setError(false);
    try {
      const data = await fetchChatConversations();
      setConversations(data.items || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const handleServerEvent = (event) => {
      if (event.detail?.type?.startsWith('chat.')) {
        loadConversations();
      }

      if (event.detail?.type === 'presence.updated') {
        setConversations((current) =>
          current.map((conversation) => ({
            ...conversation,
            participants: (conversation.participants || []).map((participant) =>
              participant.id === event.detail.userId
                ? { ...participant, presence: event.detail.presence }
                : participant
            ),
          }))
        );
      }
    };

    window.addEventListener('thetavern:server-event', handleServerEvent);
    return () => window.removeEventListener('thetavern:server-event', handleServerEvent);
  }, [loadConversations]);

  useEffect(() => {
    if (!requestedConversationId) {
      return;
    }

    if (chatContext?.conversationId === requestedConversationId) {
      if (pushScrollToken && chatContext.scrollToBottomToken !== pushScrollToken) {
        setChatContext((current) => ({
          ...current,
          scrollToBottomToken: pushScrollToken,
        }));
      }
      return;
    }

    if (!pushScrollToken && closedConversationIdRef.current === requestedConversationId) {
      return;
    }

    closedConversationIdRef.current = null;
    setChatContext({
      conversationId: requestedConversationId,
      scrollToBottomToken: pushScrollToken || undefined,
    });
  }, [chatContext?.conversationId, chatContext?.scrollToBottomToken, pushScrollToken, requestedConversationId]);

  const grouped = useMemo(
    () => ({
      direct: conversations.filter((conversation) => conversation.type === 'direct'),
      tables: conversations.filter((conversation) => conversation.type !== 'direct'),
    }),
    [conversations]
  );

  const openConversation = (conversation) => {
    closedConversationIdRef.current = null;
    setChatContext({ conversationId: conversation.id });
    setSearchParams({ conversationId: String(conversation.id) });
  };

  const closeConversation = () => {
    closedConversationIdRef.current = chatContext?.conversationId || requestedConversationId || null;
    setChatContext(null);
    setSearchParams({}, { replace: true });
    loadConversations();
  };

  const loadStartCompanions = useCallback(async ({ page = 1, query = '', mode = 'replace' } = {}) => {
    const requestId = startCompanionsRequestRef.current + 1;
    startCompanionsRequestRef.current = requestId;
    setStartCompanionsLoading(true);

    try {
      const data = await fetchCompanions({
        limit: START_COMPANIONS_PAGE_SIZE,
        page,
        query,
      });
      if (requestId !== startCompanionsRequestRef.current) return;
      setStartCompanions((current) =>
        mode === 'append' ? [...current, ...(data.items || [])] : data.items || []
      );
      setStartCompanionsPage(data.page || page);
      setStartCompanionsHasMore(Boolean(data.hasMore));
    } catch {
      if (requestId !== startCompanionsRequestRef.current) return;
      if (mode !== 'append') setStartCompanions([]);
      setStartCompanionsHasMore(false);
    } finally {
      if (requestId === startCompanionsRequestRef.current) {
        setStartCompanionsLoading(false);
      }
    }
  }, []);

  const openStartDialog = async () => {
    setStartDialogOpen(true);
    setStartDialogLoading(true);
    setStartCompanionQuery('');
    setStartCompanions([]);
    setStartCompanionsHasMore(false);
    skipNextStartCompanionSearchRef.current = true;
    loadStartCompanions({ page: 1, query: '' });

    try {
      const nightsData = await fetchNights({ scope: 'future', limit: 50 });
      setStartNights((nightsData.items || []).filter((night) => night.status !== 'deleted'));
    } catch {
      setStartNights([]);
    } finally {
      setStartDialogLoading(false);
    }
  };

  const closeStartDialog = () => {
    setStartDialogOpen(false);
  };

  useEffect(() => {
    if (!startDialogOpen) return undefined;
    if (skipNextStartCompanionSearchRef.current) {
      skipNextStartCompanionSearchRef.current = false;
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      loadStartCompanions({
        page: 1,
        query: startCompanionQuery,
      });
    }, 240);

    return () => window.clearTimeout(timeoutId);
  }, [loadStartCompanions, startCompanionQuery, startDialogOpen]);

  const loadMoreStartCompanions = () => {
    if (startCompanionsLoading || !startCompanionsHasMore) return;

    loadStartCompanions({
      page: startCompanionsPage + 1,
      query: startCompanionQuery,
      mode: 'append',
    });
  };

  const startDirectChat = (companion) => {
    closeStartDialog();
    closedConversationIdRef.current = null;
    setChatContext({
      type: 'direct',
      userId: companion.id,
      title: companion.displayName || companion.username,
    });
    setSearchParams({}, { replace: true });
  };

  const startNightChat = (night) => {
    closeStartDialog();
    closedConversationIdRef.current = null;
    setChatContext({
      type: 'night',
      nightId: night.id,
      title: t('chat.context.nightTitle', { title: night.title }),
      subtitle: t('chat.context.nightSubtitle'),
    });
    setSearchParams({}, { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        background:
          'radial-gradient(circle at 14% 8%, rgba(236, 170, 65, 0.18), transparent 25rem), radial-gradient(circle at 88% 18%, rgba(15, 76, 69, 0.10), transparent 28rem)',
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, md: 5 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          sx={{ mb: { xs: 4, md: 5 } }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'secondary.dark', fontWeight: 900, letterSpacing: '0.14em' }}
            >
              {t('chat.inbox.eyebrow')}
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '3rem', md: '4.7rem' } }}>
              {t('chat.inbox.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 620 }}>
              {t('chat.inbox.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={openStartDialog}
          >
            {t('chat.inbox.start')}
          </Button>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.error.light, 0.08),
              p: 3,
            }}
          >
            <Typography>{t('chat.inbox.loadError')}</Typography>
          </Box>
        ) : (
          <>
            <ConversationSection
              conversations={grouped.direct}
              empty={t('chat.inbox.emptyDirectSection')}
              icon={<PersonOutlineIcon color="primary" />}
              onOpen={openConversation}
              title={t('chat.inbox.direct')}
              t={t}
            />
            <ConversationSection
              conversations={grouped.tables}
              empty={t('chat.inbox.emptyTableSection')}
              icon={<GroupIcon color="primary" />}
              onOpen={openConversation}
              title={t('chat.inbox.tables')}
              t={t}
            />
          </>
        )}
      </Box>

      <TavernChatPanel
        context={chatContext || {}}
        open={Boolean(chatContext)}
        onClose={closeConversation}
      />
      <StartChatDialog
        companions={startCompanions}
        companionsHasMore={startCompanionsHasMore}
        companionsLoading={startCompanionsLoading}
        loading={startDialogLoading}
        nights={startNights}
        onClose={closeStartDialog}
        onLoadMoreCompanions={loadMoreStartCompanions}
        onSearchCompanions={setStartCompanionQuery}
        onSelectCompanion={startDirectChat}
        onSelectNight={startNightChat}
        open={startDialogOpen}
        searchQuery={startCompanionQuery}
        language={i18n.language}
        t={t}
      />
    </Box>
  );
};

export default ChatPage;
